/**
 * <bp-theme-toggle> — light/dark switch for the blueprint chrome.
 *
 * Persists the choice in localStorage under key `bp-theme` and toggles the
 * `html.dark` class so tokens.css swaps the palette. Server-rendered initial
 * class is set by shared/_theme_script.html.erb before this element upgrades.
 */

const STORAGE_KEY = 'bp-theme';

export class BpThemeToggle extends HTMLElement {
  private button: HTMLButtonElement | null = null;

  connectedCallback() {
    this.button = this.querySelector('button') ?? (this as unknown as HTMLButtonElement);
    if (!this.button) return;
    this.button.addEventListener('click', this.onClick);
    this.syncIcon();
  }

  disconnectedCallback() {
    this.button?.removeEventListener('click', this.onClick);
  }

  private onClick = () => {
    const next = document.documentElement.classList.contains('dark') ? 'light' : 'dark';
    this.apply(next);
  };

  private apply(theme: 'light' | 'dark') {
    document.documentElement.classList.toggle('dark', theme === 'dark');
    try {
      localStorage.setItem(STORAGE_KEY, theme);
    } catch {
      // quota / private-mode — ignore
    }
    this.syncIcon();
    this.dispatchEvent(new CustomEvent('bp-theme-change', { detail: { theme }, bubbles: true }));
  }

  private syncIcon() {
    if (!this.button) return;
    const isDark = document.documentElement.classList.contains('dark');
    const glyph = this.button.querySelector('[aria-hidden="true"]');
    if (glyph) glyph.textContent = isDark ? '◑' : '◐';
  }
}

if (!customElements.get('bp-theme-toggle')) {
  customElements.define('bp-theme-toggle', BpThemeToggle);
}
